import React, { useState, useEffect } from 'react';
import {
  Users,
  Lightbulb,
  MessageSquare,
  Briefcase,
  Monitor,
  Rocket
} from 'lucide-react';

// ─── Direct Layout & Core Imports (Bypasses barrel re-exports) ───
import Navbar from './modules/shared/components/layout/Navbar';
import Footer from './modules/shared/components/layout/Footer';
import Home from './modules/shared/pages/Home';
import Loader3D from './modules/shared/components/Loader3D';
import API_BASE_URL from './config';

// ─── Route-Based Dynamic Code-Splitting ───
const EnergySourcingFooter = React.lazy(() => import('./modules/energySourcing/components/EnergySourcingFooter'));

// ─── Route-Based Dynamic Code-Splitting (Reduces main.js initial bundle by >60%) ───
const AboutUs = React.lazy(() => import('./modules/shared/pages/AboutUs'));
const Careers = React.lazy(() => import('./modules/shared/pages/Careers'));
const Contact = React.lazy(() => import('./modules/shared/pages/Contact'));
const FAQ = React.lazy(() => import('./modules/shared/pages/FAQ'));
const HelpCenter = React.lazy(() => import('./modules/shared/pages/HelpCenter'));
const AuthPage = React.lazy(() => import('./modules/shared/pages/AuthPage'));
const Cart = React.lazy(() => import('./modules/shared/pages/Cart'));
const PaymentPage = React.lazy(() => import('./modules/shared/pages/PaymentPage'));
const AdminDashboard = React.lazy(() => import('./modules/shared/pages/AdminDashboard'));

// ─── Department 1: Energy Sourcing ───
const EnergySourcing = React.lazy(() => import('./modules/energySourcing/pages/EnergySourcing'));
const EngineeringHub = React.lazy(() => import('./modules/energySourcing/pages/EngineeringHub'));

// ─── Department 2: IT & Cloud Lab Solutions ───
const Lab = React.lazy(() => import('./modules/itSolutions/pages/Lab'));
const DemoLab = React.lazy(() => import('./modules/itSolutions/pages/DemoLab'));
const LabSpecs = React.lazy(() => import('./modules/itSolutions/pages/LabSpecs'));
const Solutions = React.lazy(() => import('./modules/itSolutions/pages/Solutions'));
const SetupGuide = React.lazy(() => import('./modules/itSolutions/pages/SetupGuide'));
const Troubleshoot = React.lazy(() => import('./modules/itSolutions/pages/Troubleshoot'));
const LabDashboard = React.lazy(() => import('./modules/itSolutions/components/LabDashboard'));
const LabComingSoon = React.lazy(() => import('./modules/itSolutions/pages/LabComingSoon'));

// Particle Background Component — Memoized to avoid re-generating particles on every re-render
const ParticleBackground = React.memo(() => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const particles = React.useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: 2 + Math.random() * 4,
    duration: 12 + Math.random() * 18,
    delay: Math.random() * 8,
    opacity: 0.15 + Math.random() * 0.3,
    wobble: -30 + Math.random() * 60,
    color: Math.random() > 0.8 ? 'var(--primary-blue)' : '#94a3b8',
  })), []);

  if (!ready) return null;

  return (
    <div className="particles-container">
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: p.left,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.color,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: p.opacity,
            '--wobble': `${p.wobble}px`,
          }}
        />
      ))}
    </div>
  );
});

const App = () => {
  const [activeTab, setActiveTabState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get('tab');
    if (urlTab) return urlTab;
    return localStorage.getItem('activeTab') || 'home';
  });
  const [previousTab, setPreviousTab] = useState(() => {
    return localStorage.getItem('previousTab') || 'home';
  });
  
  const [pendingScrollTarget, setPendingScrollTarget] = useState(null);

  const setActiveTab = (newTab, targetId = null) => {
    if (targetId) {
      setPendingScrollTarget(targetId);
    } else {
      setPendingScrollTarget(null);
    }
    if (activeTab !== newTab) {
      setPreviousTab(activeTab);
      setActiveTabState(newTab);
      localStorage.setItem('activeTab', newTab);
      localStorage.setItem('previousTab', activeTab);
      try {
        const url = new URL(window.location);
        url.searchParams.set('tab', newTab);
        window.history.replaceState({}, '', url);
      } catch (e) {}
    } else if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const [pageKey, setPageKey] = useState(0);
  const [selectedTroubleshootOs, setSelectedTroubleshootOs] = useState('Windows');
  
  // Session state
  const [user, setUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const saved = localStorage.getItem('selectedPlan');
    return saved ? JSON.parse(saved) : null;
  });
  const [purchasedPlan, setPurchasedPlan] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Fetch profile dynamically on startup
  useEffect(() => {
    // Migration: Clean up any old user/plan data previously saved in localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('purchasedPlan');

    const token = localStorage.getItem('token');
    if (token) {
      setLoadingProfile(true);
      fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            const err = new Error('Session invalid or expired');
            err.status = res.status;
            throw err;
          }
          throw new Error('Server temporarily unavailable');
        }
        return res.json();
      })
      .then(data => {
        setUser(data.user);
        setPurchasedPlan(data.subscription);
      })
      .catch(err => {
        console.error('Failed to restore session:', err.message);
        // Only remove token if the session is explicitly invalid (unauthorized)
        if (err.status === 401 || err.status === 403) {
          localStorage.removeItem('token');
          setUser(null);
          setPurchasedPlan(null);
        }
      })
      .finally(() => {
        setLoadingProfile(false);
      });
    } else {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPlan) localStorage.setItem('selectedPlan', JSON.stringify(selectedPlan));
    else localStorage.removeItem('selectedPlan');
  }, [selectedPlan]);

  // Check expiration on tab change or periodically
  useEffect(() => {
    if (purchasedPlan && purchasedPlan.endDate) {
      if (new Date() > new Date(purchasedPlan.endDate)) {
        setPurchasedPlan(null);
      }
    }
  }, [activeTab, purchasedPlan]);



  const menuItems = [
    { id: 'solutions', label: 'Solutions', icon: Lightbulb },
    { id: 'engineering-hub', label: 'Engineering Hub', icon: Rocket },
    { id: 'lab', label: 'Lab Access', icon: Monitor },
    { id: 'contact', label: 'Contact', icon: MessageSquare },
    { id: 'careers', label: 'Careers', icon: Briefcase },
    { id: 'aboutus', label: 'About', icon: Users },
  ];

  const isFirstMount = React.useRef(true);

  // Re-trigger page animation on tab change & scroll smoothly to target or top
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPageKey(prev => prev + 1);
    if (pendingScrollTarget) {
      const timer = setTimeout(() => {
        const el = document.getElementById(pendingScrollTarget);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setPendingScrollTarget(null);
      }, 120);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab, pendingScrollTarget]);

  // Universal Ultra-Smooth Section Scroll Reveal across all pages
  useEffect(() => {
    let observer;
    const handleScrollReveal = () => {
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('mpx-revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.05,
          rootMargin: '0px 0px -40px 0px'
        }
      );

      const elements = document.querySelectorAll(
        'section:not(.home-hero-section):not(#home-hero-section):not(.es-hero-cinematic):not(#es-hero-cinematic), [data-section-reveal]'
      );

      elements.forEach((el) => {
        el.classList.add('mpx-scroll-reveal');
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.92) {
          el.classList.add('mpx-revealed');
        } else {
          observer.observe(el);
        }
      });
    };

    const timer = setTimeout(handleScrollReveal, 150);

    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [activeTab, pageKey]);

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'solutions':
        return <Solutions setActiveTab={setActiveTab} user={user} />;
      case 'engineering-hub':
        return <EngineeringHub setActiveTab={setActiveTab} />;
      case 'lab':
        return <Lab setActiveTab={setActiveTab} setSelectedPlan={setSelectedPlan} user={user} purchasedPlan={purchasedPlan} setSelectedTroubleshootOs={setSelectedTroubleshootOs} />;
      case 'contact':
        return <Contact setActiveTab={setActiveTab} />;
      case 'careers':
        return <Careers setActiveTab={setActiveTab} user={user} />;
      case 'aboutus':
        return <AboutUs setActiveTab={setActiveTab} />;
      case 'manpower-sourcing':
        return <EnergySourcing setActiveTab={setActiveTab} user={user} setUser={setUser} />;
      case 'cart':
        return <Cart setActiveTab={setActiveTab} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} user={user} />;
      case 'auth':
        return <AuthPage setActiveTab={setActiveTab} previousTab={previousTab} user={user} setUser={setUser} setPurchasedPlan={setPurchasedPlan} />;
      case 'payment':
        return <PaymentPage setActiveTab={setActiveTab} selectedPlan={selectedPlan} user={user} setUser={setUser} setPurchasedPlan={setPurchasedPlan} />;
      case 'admin':
        return <AdminDashboard user={user} />;
      case 'lab-dashboard':
        return <LabDashboard user={user} purchasedPlan={purchasedPlan} setActiveTab={setActiveTab} setSelectedTroubleshootOs={setSelectedTroubleshootOs} setSelectedPlan={setSelectedPlan} />;
      case 'troubleshoot':
        return <Troubleshoot activeOs={selectedTroubleshootOs} setActiveOs={setSelectedTroubleshootOs} setActiveTab={setActiveTab} />;
      case 'faq':
        return <FAQ setActiveTab={setActiveTab} />;
      case 'helpcenter':
        return <HelpCenter setActiveTab={setActiveTab} />;
      case 'setup-guide':
        return <SetupGuide setActiveTab={setActiveTab} user={user} />;
      case 'demolab':
        return <DemoLab setActiveTab={setActiveTab} user={user} />;
      case 'specs':
      case 'lab-specs':
        return <LabSpecs setActiveTab={setActiveTab} />;
      case 'lab-coming-soon':
        return <LabComingSoon selectedPlan={selectedPlan} setActiveTab={setActiveTab} user={user} />;
      case 'home':
      default:
        return <Home setActiveTab={setActiveTab} />;
    }
  };

  if (loadingProfile && localStorage.getItem('token')) {
    return <Loader3D />;
  }

  return (
    <div className="min-h-screen flex flex-col font-sans" style={{ background: 'var(--bg-light)' }}>
      {/* Neon glow removed */}

      {/* Particle Background */}
      <ParticleBackground />

      {activeTab !== 'manpower-sourcing' && (
        <Navbar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          menuItems={menuItems}
          user={user}
          setUser={setUser}
          purchasedPlan={purchasedPlan}
          setPurchasedPlan={setPurchasedPlan}
        />
      )}

      {/* Main Content Area */}
      <main className={`flex-1 ${activeTab !== 'manpower-sourcing' ? 'mt-[116px] sm:mt-[120px] lg:mt-[126px]' : 'mt-0'} relative z-10 w-full`}>
        <div key={pageKey} className="w-full page-content">
          <React.Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><Loader3D /></div>}>
            {renderActiveComponent()}
          </React.Suspense>
        </div>
        {activeTab === 'manpower-sourcing' ? (
          <React.Suspense fallback={null}>
            <EnergySourcingFooter setActiveTab={setActiveTab} />
          </React.Suspense>
        ) : (
          <Footer setActiveTab={setActiveTab} />
        )}
      </main>
    </div>
  );
};

export default App;
